// React and hooks
import { useEffect, useState } from 'react';

// External libraries
import { useQuery } from '@tanstack/react-query';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Table, Button, Tooltip, Row, Spin } from 'antd';
import dayjs from 'dayjs';

// Types and services
import {
	AccountsService,
	ImportsService,
	ImportStatus,
	ImportItem,
} from 'modules/escrow/services';

// Utils
import { downloadCsvTemplate } from 'modules/escrow/utils/downloadCsvTemplate';
import {
	getAccountRouteTypeIcon,
	getAccountRouteTypeName,
	useIsEscrowAccountRoute,
} from 'modules/escrow/utils/adminRoute';

// Components
import { PageHeader } from 'components/PageHeader';
import { Heading1 } from 'components/Heading1';
import { BreadcrumbContainer } from 'components/Breadcrumb';
import { SplashScreen } from 'components/SplashScreen';
import { TextM, TextS } from 'components/Text';
import ImportCsvModal from './ components/ImportCsvModal';
import { ImportCsvHelperModal } from 'components/ImportCsvHelperModal';

// Context
import { useBreadcrumb } from 'modules/core/context/AppContext';

// Theme and styles
import theme from 'theme/theme';
import { StyledContainer } from './styles';

// Constants
import { EscrowRoutes } from 'modules/escrow/constants/routes';
import { BATCH_TRANSFERS_ENABLED } from 'modules/escrow/constants/featureFlags';

// Icons
import {
	IoAdd,
	IoArrowDown,
	IoCheckmarkCircleOutline,
	IoInformationCircleOutline,
	IoTimeOutline,
} from 'react-icons/io5';

const EscrowImportsPage = () => {
	const { type, id } = useParams();
	const isEscrowRoute = useIsEscrowAccountRoute();
	const { setBreadcrumb } = useBreadcrumb();
	const navigate = useNavigate();
	const [isModalCsvOpen, setIsModalCsvOpen] = useState<boolean>(false);
	const [isHelperModalOpen, setIsHelperModalOpen] = useState<boolean>(false);

	const { data: importsData, isLoading: importsLoading } = useQuery({
		queryKey: ['imports', type],
		queryFn: () =>
			ImportsService.getImports({
				type: type?.toLocaleUpperCase() as 'DESTINATION' | 'TRANSFER',
				accountId: id!,
			}),
		refetchInterval: query => {
			const data = query.state.data;
			if (!Array.isArray(data)) return false;

			const hasProcessing = data.some(
				(item: { status: ImportStatus }) =>
					item.status === 'PENDING' || item.status === 'PROCESSING',
			);

			return hasProcessing ? 5000 : false;
		},
	});

	const { data: account } = useQuery({
		queryKey: ['accountDetails', id],
		queryFn: () => AccountsService.getAccount(id!),
		enabled: !!id,
	});

	const redirectAccount = isEscrowRoute
		? EscrowRoutes.ESCROW_BASE
		: EscrowRoutes.ACCOUNT_BASE;
	const title = (
		<BreadcrumbContainer>
			{getAccountRouteTypeIcon()}
			<span>{getAccountRouteTypeName()}</span>
		</BreadcrumbContainer>
	);

	useEffect(() => {
		if (!account) return;

		setBreadcrumb([
			{
				href: redirectAccount,
				title: title,
			},
			{
				href: `${redirectAccount}/${id}/details`,
				title: (
					<TextM color={theme.textGray}>
						{isEscrowRoute ? 'Conta Escrow ' : 'Conta '}
						{account?.person?.name ?? ''}
					</TextM>
				),
			},
			{
				title: (
					<TextM color={theme.textGray}>
						{type === 'destination'
							? 'Cadastros de conta beneficiária em lote'
							: 'Transferências em lote'}
					</TextM>
				),
			},
		]);
	}, [account, redirectAccount]);

	if (!['destination', 'transfer'].includes(type || '')) {
		return <Navigate to={EscrowRoutes.ESCROW_BASE} />;
	}

	// Bloqueia o acesso direto por URL à importação de transferências em lote
	// enquanto a feature está desabilitada (ver BATCH_TRANSFERS_ENABLED).
	if (!BATCH_TRANSFERS_ENABLED && type === 'transfer') {
		return <Navigate to={EscrowRoutes.ESCROW_BASE} />;
	}

	if (importsLoading) return <SplashScreen />;

	const hasTransferInProgress =
		type === 'transfer' &&
		(importsData || []).some(
			item => item.status === 'PENDING' || item.status === 'PROCESSING',
		);

	const titleMap = {
		destination: 'Cadastros de conta beneficiária em lote',
		transfer: 'Transferências em lote',
	};

	const subtitleMap = {
		destination: 'Cadastros em lote',
		transfer: 'Transferências em lote',
	};

	const getStatusConfig = (record: ImportItem) => {
		const { status, error } = record;

		switch (status) {
			case 'PENDING':
				return {
					label: 'Em fila de processamento',
					icon: (
						<IoTimeOutline size={'1.4rem'} color={theme.textGray} />
					),
				};

			case 'PROCESSING':
				return {
					label: `${record.lines_processed} / ${record.total_lines} linhas processadas`,
					icon: <Spin size="small" />,
				};

			case 'FINISHED':
				return {
					label: 'Arquivo gerado com sucesso',
					icon: (
						<IoCheckmarkCircleOutline
							size={'1.4rem'}
							color={theme.textGray}
						/>
					),
				};

			case 'ERROR':
				return {
					label: `Erro: ${error || 'Falha ao processar'}`,
					icon: (
						<IoInformationCircleOutline
							size={'1.4rem'}
							color={theme.textGray}
						/>
					),
				};

			default:
				return {
					label: status,
					icon: null,
				};
		}
	};

	const columns = [
		{
			title: 'Data de processamento',
			dataIndex: 'created_at',
			width: '28%',
			render: (value: string) => {
				const date = dayjs(value).format('DD/MM/YYYY');
				return <TextS color={theme.textGray}>{date}</TextS>;
			},
		},
		{
			title: 'Hora de processamento',
			dataIndex: 'created_at',
			width: '28%',
			render: (value: string) => {
				const time = dayjs(value).format('HH:mm:ss');
				return <TextS color={theme.textGray}>{time}</TextS>;
			},
		},
		{
			title: 'Quantidade',
			dataIndex: 'total_lines',
			render: (total_lines: number) => {
				return <TextS color={theme.textGray}>{total_lines}</TextS>;
			},
			width: '28%',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: (status: ImportStatus, record: ImportItem) => {
				const { label, icon } = getStatusConfig(record);

				if (status === 'ERROR') {
					return (
						<Tooltip title={record.error}>
							<Row align="middle" style={{ gap: 8 }} wrap={false}>
								{icon}
								<TextS
									color={theme.textGray}
									style={{
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										maxWidth: '150px',
									}}
								>
									{label}
								</TextS>
							</Row>
						</Tooltip>
					);
				}

				return (
					<Row align="middle" style={{ gap: 8 }} wrap={false}>
						{icon}
						<TextS
							color={theme.textGray}
							style={{
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
						>
							{label}
						</TextS>
					</Row>
				);
			},
		},
	];

	return (
		<>
			{/* <PageHeader isFluid>
				<Heading1>
					{titleMap[type as 'destination' | 'transfer']}
				</Heading1>
			</PageHeader> */}

			<StyledContainer>
				<Row
					justify="space-between"
					align="middle"
					style={{ marginBottom: '1rem' }}
				>
					<Heading1 style={{ fontWeight: 400 }}>
						{subtitleMap[type as 'destination' | 'transfer']}
					</Heading1>

					<Row justify="end" style={{ gap: '1rem' }}>
						<Button
							type="default"
							onClick={downloadCsvTemplate}
							icon={
								<IoArrowDown
									size={'1rem'}
									color={theme.primary}
								/>
							}
							iconPosition="end"
						>
							<TextS color={theme.primary}>
								Fazer download do template
							</TextS>
						</Button>
						<Tooltip
							title={
								hasTransferInProgress
									? 'Já existe uma transferência em lote sendo processada. Aguarde a conclusão para enviar uma nova.'
									: ''
							}
						>
							<Button
								type="primary"
								disabled={hasTransferInProgress}
								onClick={() => setIsModalCsvOpen(true)}
								icon={
									<IoAdd
										size={'1rem'}
										color={
											hasTransferInProgress
												? theme.textGray
												: theme.white
										}
									/>
								}
								iconPosition="end"
							>
								<TextS
									color={
										hasTransferInProgress
											? theme.textGray
											: theme.white
									}
								>
									{type === 'destination'
										? 'Novo cadastro de contas em lote'
										: 'Nova transferência em lote'}
								</TextS>
							</Button>
						</Tooltip>
						<Button
							type="default"
							onClick={() => setIsHelperModalOpen(true)}
							icon={
								<IoInformationCircleOutline
									size={'1.1rem'}
									color={theme.primary}
								/>
							}
							title="Guia de preenchimento do CSV"
							aria-label="Guia de preenchimento do CSV"
						/>
					</Row>
				</Row>
				<Table
					rowKey="id"
					dataSource={importsData || []}
					columns={columns}
					pagination={{ pageSize: 10 }}
					onRow={(record: any) => ({
						onClick: () => {
							if (
								record.status === 'PENDING' ||
								record.status === 'PROCESSING'
							) {
								return;
							}
							navigate(
								`${redirectAccount}/${id}/details/imports/${type}/${record.id}`,
							);
						},
						style: {
							cursor:
								record.status === 'PENDING' ||
								record.status === 'PROCESSING'
									? 'default'
									: 'pointer',
						},
					})}
				/>
			</StyledContainer>

			<ImportCsvModal
				type={type as 'destination' | 'transfer'}
				isOpen={isModalCsvOpen}
				onClose={() => setIsModalCsvOpen(false)}
			/>

			<ImportCsvHelperModal
				isOpen={isHelperModalOpen}
				onClose={() => setIsHelperModalOpen(false)}
			/>
		</>
	);
};

export default EscrowImportsPage;
